import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { uploadManagedFile } from "@/utils/managedFile";
import type { ScanStatus } from "@/components/ui/ManagedFileLink";

/**
 * Moduł Pozyskiwanie — baza firm, których jeszcze nie mamy w systemie.
 *
 * Wszystkie mutacje unieważniają klucz ["leads"], bo prawie każda zmienia dwie
 * rzeczy naraz: kartę firmy i oś czasu na niej. Rozdzielanie tego na precyzyjne
 * unieważnienia dawało karty, na których zadanie było już zrobione, a wpis o tym
 * jeszcze nie istniał.
 */

export type LeadStatus =
  | "new"
  | "contacted"
  | "interested"
  | "converted"
  | "rejected"
  /** Nasz werdykt: nie chcemy z tą firmą pracować (≠ „rejected" = ich odmowa). */
  | "declined"
  | "blocked";

export type LeadSource = "manual" | "ai_search" | "referral" | "event" | "inbound" | "other";

export type ActivityKind =
  | "note"
  | "call"
  | "meeting"
  | "email_out"
  | "email_in"
  | "status_change"
  | "task"
  | "file"
  | "system";

export interface LeadContact {
  id: string;
  first_name: string;
  last_name: string;
  /** "" | "pan" | "pani" — pusta wartość znaczy „Szanowni Państwo". */
  honorific: string;
  /** Gotowy zwrot, jaki wyjdzie w mailu — liczony przez backend. */
  salutation: string;
  /** Ręczna korekta zwrotu; niepusta wygrywa z wyliczoną. */
  salutation_override: string;
  position: string;
  email: string;
  phone: string;
  linkedin_url: string;
  note: string;
  is_primary: boolean;
  display_name: string;
  is_blocked: boolean;
  created_at: string;
}

export interface LeadRow {
  id: string;
  name: string;
  nip: string;
  city: string;
  website: string;
  voivodeships: string[];
  voivodeship_labels?: string[];
  status: LeadStatus;
  status_label: string;
  source: LeadSource;
  source_label: string;
  /** Zlecenie wyszukiwania, z którego firma weszła do bazy (null = dodana inaczej). */
  source_search: { id: string; prompt: string } | null;
  owner: string | null;
  owner_name: string | null;
  primary_contact: {
    id: string;
    display_name: string;
    position: string;
    email: string;
    phone: string;
  } | null;
  open_tasks_count: number;
  overdue_tasks_count: number;
  contractor: string | null;
  contractor_name: string | null;
  last_activity_at: string | null;
  last_activity_by_name: string | null;
  created_at: string;
}

export interface LeadTask {
  id: string;
  lead: string;
  lead_name: string;
  title: string;
  description: string;
  due_at: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  done_at: string | null;
  done_by_name: string | null;
  is_done: boolean;
  is_overdue: boolean;
  created_at: string;
}

export interface LeadAttachment {
  id: string;
  managed_file: string;
  activity: string | null;
  label: string;
  original_filename: string;
  size_bytes: number | null;
  scan_status: ScanStatus;
  url: string | null;
  created_by_name: string | null;
  created_at: string;
}

export interface LeadActivity {
  id: string;
  kind: ActivityKind;
  kind_label: string;
  title: string;
  body: string;
  contact: string | null;
  contact_name: string | null;
  occurred_at: string;
  is_automatic: boolean;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_by_name: string | null;
  attachments: LeadAttachment[];
  created_at: string;
}

export interface Lead extends LeadRow {
  regon: string;
  krs: string;
  street: string;
  postal_code: string;
  source_detail: string;
  notes: string;
  converted_at: string | null;
  contacts: LeadContact[];
  tasks: LeadTask[];
  attachments: LeadAttachment[];
  suppression: { id: string; nip: string; email: string; reason: string; created_at: string } | null;
  /** Powód decyzji „nie chcemy współpracować" — pusty przy każdym innym statusie. */
  decline_reason: string;
  existing_contractor: { id: string; name: string; is_active: boolean; linked: boolean } | null;
  created_by_name: string | null;
  updated_at: string;
}

export interface DoNotContactEntry {
  id: string;
  nip: string;
  email: string;
  reason: string;
  lead: string | null;
  lead_name: string | null;
  created_by_name: string | null;
  created_at: string;
}

export interface LeadFilters {
  q?: string;
  status?: string;
  owner?: string;
  source?: string;
  /** Id zlecenia wyszukiwania — tylko firmy, które z niego weszły do bazy. */
  search?: string;
  voivodeship?: string;
  open_tasks?: boolean;
  overdue?: boolean;
  include_blocked?: boolean;
  /** Odsiewa firmy będące już odbiorcami innej kampanii niż `campaign`. */
  not_in_other_campaigns?: boolean;
  /** Kampania, do której właśnie dobieramy — jej właśni odbiorcy nie są „inną". */
  campaign?: string;
  ordering?: string;
}

export interface LeadSummary {
  by_status: { value: LeadStatus; label: string; count: number }[];
  total: number;
  my_open_tasks: number;
  my_overdue_tasks: number;
  suppressed: number;
}

export interface NipCheck {
  lead: { id: string; name: string; status: LeadStatus; status_label: string } | null;
  contractor: { id: string; name: string; is_active: boolean } | null;
  suppression: { id: string; reason: string; created_at: string } | null;
  registry: {
    found: boolean;
    nip: string;
    name?: string;
    regon?: string;
    krs?: string;
    vat_status?: string;
    registered_address?: {
      street?: string;
      building_number?: string;
      apartment_number?: string;
      postal_code?: string;
      city?: string;
    } | null;
  } | null;
  registry_error: string | null;
}

export interface LeadDictionaries {
  statuses: { value: LeadStatus; label: string }[];
  sources: { value: LeadSource; label: string }[];
  activity_kinds: { value: ActivityKind; label: string }[];
  voivodeships: { value: string; label: string }[];
  /** Co przyjmie Outlook odbiorcy — lista blokad i limity liczy backend
   *  (apps.leads.mail_limits), żeby ekran nie trzymał drugiej ich kopii. */
  mail_attachments: {
    max_count: number;
    max_total_bytes: number;
    blocked_extensions: string[];
  };
}

const KEY = ["leads"];

/** `options.enabled = false` wstrzymuje zapytanie — do pól szukania, które mają
 *  milczeć, dopóki człowiek nie wpisze dość znaków. Bez tego każde wejście na
 *  ekran ciągnie całą listę firm. */
export function useLeads(filters: LeadFilters = {}, options?: { enabled?: boolean }) {
  const params: Record<string, string> = {};
  if (filters.q) params.q = filters.q;
  if (filters.status) params.status = filters.status;
  if (filters.owner) params.owner = filters.owner;
  if (filters.source) params.source = filters.source;
  if (filters.search) params.search = filters.search;
  if (filters.voivodeship) params.voivodeship = filters.voivodeship;
  if (filters.not_in_other_campaigns) {
    params.not_in_other_campaigns = "1";
    if (filters.campaign) params.campaign = filters.campaign;
  }
  if (filters.open_tasks) params.open_tasks = "1";
  if (filters.overdue) params.overdue = "1";
  if (filters.include_blocked) params.include_blocked = "1";
  if (filters.ordering) params.ordering = filters.ordering;

  return useQuery({
    queryKey: [...KEY, "list", params],
    queryFn: () =>
      apiClient.get<{ data: LeadRow[] }>("/leads/leads/", { params }).then((r) => r.data.data),
    enabled: options?.enabled ?? true,
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, "detail", id],
    enabled: !!id,
    queryFn: () =>
      apiClient.get<{ data: Lead }>(`/leads/leads/${id}/`).then((r) => r.data.data),
  });
}

export function useLeadSummary() {
  return useQuery({
    queryKey: [...KEY, "summary"],
    queryFn: () =>
      apiClient.get<{ data: LeadSummary }>("/leads/leads/summary/").then((r) => r.data.data),
  });
}

export function useLeadDictionaries() {
  return useQuery({
    queryKey: [...KEY, "dictionaries"],
    staleTime: 60 * 60 * 1000,
    queryFn: () =>
      apiClient.get<{ data: LeadDictionaries }>("/leads/dictionaries/").then((r) => r.data.data),
  });
}

/** Sprawdzenie NIP-u przed założeniem karty — duplikat, podwykonawca, blokada, rejestry. */
export function checkNip(nip: string, excludeLead?: string) {
  const params: Record<string, string> = { nip };
  if (excludeLead) params.exclude_lead = excludeLead;
  return apiClient
    .get<{ data: NipCheck }>("/leads/nip-check/", { params })
    .then((r) => r.data.data);
}

export interface LeadPayload {
  name: string;
  nip?: string;
  regon?: string;
  krs?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  website?: string;
  voivodeships?: string[];
  source?: LeadSource;
  source_detail?: string;
  owner?: string | null;
  notes?: string;
  contacts?: Partial<LeadContact>[];
}

function useLeadMutation<TArgs, TResult = unknown>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCreateLead() {
  return useLeadMutation((payload: LeadPayload) =>
    apiClient.post<{ data: Lead }>("/leads/leads/", payload).then((r) => r.data.data),
  );
}

export function useUpdateLead() {
  return useLeadMutation(({ id, ...payload }: LeadPayload & { id: string }) =>
    apiClient.patch<{ data: Lead }>(`/leads/leads/${id}/`, payload).then((r) => r.data.data),
  );
}

export function useDeleteLead() {
  return useLeadMutation((id: string) => apiClient.delete(`/leads/leads/${id}/`));
}

export function useSetLeadStatus() {
  return useLeadMutation(({ id, status, reason }: { id: string; status: LeadStatus; reason?: string }) =>
    apiClient.post(`/leads/leads/${id}/set-status/`, { status, reason: reason ?? "" }),
  );
}

export function useBlockLead() {
  return useLeadMutation(({ id, reason }: { id: string; reason?: string }) =>
    apiClient.post(`/leads/leads/${id}/block/`, { reason: reason ?? "" }),
  );
}

/* Zdjęcie zakazu kontaktu. Kasuje WSZYSTKIE wpisy tej firmy naraz (NIP + adresy) —
   po jednym z globalnej listy dawało się firmę odblokowaną w połowie. */
export function useUnblockLead() {
  return useLeadMutation(({ id, reason }: { id: string; reason?: string }) =>
    apiClient.post(`/leads/leads/${id}/unblock/`, { reason: reason ?? "" }),
  );
}

/* „Nie chcemy współpracować" — nasza decyzja. Nie trafia na globalną listę
   „nie kontaktować" (tam są sprzeciwy ODBIORCÓW), ale zatrzymuje kampanie tak
   samo. Powód jest wymagany — backend odrzuci puste. */
export function useDeclineLead() {
  return useLeadMutation(({ id, reason }: { id: string; reason: string }) =>
    apiClient.post(`/leads/leads/${id}/decline/`, { reason }),
  );
}

export function useConvertLead() {
  return useLeadMutation(({ id, contractor }: { id: string; contractor: string }) =>
    apiClient.post(`/leads/leads/${id}/convert/`, { contractor }),
  );
}

export function useAddContact() {
  return useLeadMutation(({ leadId, ...payload }: Partial<LeadContact> & { leadId: string }) =>
    apiClient.post(`/leads/leads/${leadId}/contacts/`, payload),
  );
}

export function useUpdateContact() {
  return useLeadMutation(({ id, ...payload }: Partial<LeadContact> & { id: string }) =>
    apiClient.patch(`/leads/contacts/${id}/`, payload),
  );
}

export function useDeleteContact() {
  return useLeadMutation((id: string) => apiClient.delete(`/leads/contacts/${id}/`));
}

export function useLeadActivities(leadId: string | undefined, kinds?: string) {
  const params: Record<string, string> = { page_size: "200" };
  if (kinds) params.kind = kinds;
  return useQuery({
    queryKey: [...KEY, "activities", leadId, kinds ?? ""],
    enabled: !!leadId,
    queryFn: () =>
      apiClient
        .get<{ data: LeadActivity[] }>(`/leads/leads/${leadId}/activities/`, { params })
        .then((r) => r.data.data),
  });
}

export function useAddActivity() {
  return useLeadMutation(
    ({
      leadId,
      ...payload
    }: {
      leadId: string;
      kind: ActivityKind;
      title?: string;
      body?: string;
      contact?: string | null;
      occurred_at?: string | null;
    }) =>
      apiClient
        .post<{ data: LeadActivity }>(`/leads/leads/${leadId}/activities/`, payload)
        .then((r) => r.data.data),
  );
}

export function useLeadTasks(params: { assigned?: string; open?: boolean; lead?: string } = {}) {
  const query: Record<string, string> = {};
  if (params.assigned) query.assigned = params.assigned;
  if (params.open) query.open = "1";
  if (params.lead) query.lead = params.lead;
  return useQuery({
    queryKey: [...KEY, "tasks", query],
    queryFn: () =>
      apiClient.get<{ data: LeadTask[] }>("/leads/tasks/", { params: query }).then((r) => r.data.data),
  });
}

export function useCreateTask() {
  return useLeadMutation(
    (payload: { lead: string; title: string; description?: string; due_at: string; assigned_to?: string | null }) =>
      apiClient.post("/leads/tasks/", payload),
  );
}

export function useCompleteTask() {
  return useLeadMutation(({ id, note }: { id: string; note?: string }) =>
    apiClient.post(`/leads/tasks/${id}/complete/`, { note: note ?? "" }),
  );
}

export function useReopenTask() {
  return useLeadMutation((id: string) => apiClient.post(`/leads/tasks/${id}/reopen/`, {}));
}

export function useDeleteTask() {
  return useLeadMutation((id: string) => apiClient.delete(`/leads/tasks/${id}/`));
}

export function useUploadLeadFile() {
  return useLeadMutation(
    async ({
      leadId,
      file,
      label,
      activityId,
    }: {
      leadId: string;
      file: File;
      label?: string;
      /** Zakotwiczenie pliku pod konkretnym wpisem historii kontaktu. */
      activityId?: string;
    }) => {
      const uploaded = await uploadManagedFile(file);
      return apiClient.post(`/leads/leads/${leadId}/attachments/`, {
        managed_file: uploaded.id,
        label: label ?? "",
        ...(activityId ? { activity: activityId } : {}),
      });
    },
  );
}

export function useDeleteLeadFile() {
  return useLeadMutation((id: string) => apiClient.delete(`/leads/attachments/${id}/`));
}

export function useDoNotContact(search: string) {
  return useQuery({
    queryKey: [...KEY, "dnc", search],
    queryFn: () =>
      apiClient
        .get<{ data: DoNotContactEntry[] }>("/leads/do-not-contact/", {
          params: search ? { q: search } : {},
        })
        .then((r) => r.data.data),
  });
}

export function useAddSuppression() {
  return useLeadMutation((payload: { nip?: string; email?: string; reason?: string }) =>
    apiClient.post("/leads/do-not-contact/", payload),
  );
}

export function useDeleteSuppression() {
  return useLeadMutation((id: string) => apiClient.delete(`/leads/do-not-contact/${id}/`));
}

/** Wspólne kolory statusów — używane i na liście, i na karcie. */
export const STATUS_COLORS: Record<LeadStatus, "gray" | "blue" | "green" | "red" | "yellow" | "black"> = {
  new: "gray",
  contacted: "blue",
  interested: "yellow",
  converted: "green",
  rejected: "gray",
  declined: "black",
  blocked: "red",
};


/** Lejek na liczbach — ile firm znaleziono, do ilu napisano, ile odpisało, ile weszło. */
export interface FunnelRow {
  leads: number;
  mailed: number;
  replied: number;
  /** Odbicia — jedyny sygnał dostarczalności, jaki dziś mamy. */
  bounced: number;
  converted: number;
  rejected: number;
  /** Odpadły z naszej decyzji, nie z ich odmowy. */
  declined: number;
  blocked: number;
}

export interface FunnelSourceRow extends FunnelRow {
  source: string;
  label: string;
}

export interface LeadFunnel {
  total: FunnelRow;
  by_source: FunnelSourceRow[];
  search_candidates: { proposed: number; accepted: number; rejected: number };
  /** Los pojedynczych wysyłek — liczony na mailach, nie firmach. */
  delivery: {
    sent: number;
    delivered: number;
    hard_bounced: number;
    soft_bounced: number;
    unknown: number;
  };
}

export function useLeadFunnel() {
  return useQuery({
    queryKey: ["leads", "funnel"],
    queryFn: () =>
      apiClient.get<{ data: LeadFunnel }>("/leads/leads/funnel/").then((r) => r.data.data),
  });
}
